"use client";
import React, { useState, useMemo, useEffect } from "react";
import {
  Plus, X, Search, Edit2, Trash2, CheckCircle,
  XCircle, ToggleLeft, ToggleRight, FolderOpen,
  ShieldCheck, Ban, Layers, Eye,
  ChevronsLeft, ChevronsRight
} from "lucide-react";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { useAssetCategories } from "../../context/AssetCategoryContext";
import { useAuth } from "../../context/AuthContext";

// ─── Shared style tokens ──────────────────────────────────────────────────
const gradientStyle = { background: "linear-gradient(135deg, #2563eb, #d12465)" };
const gradientBtn =
  "text-white px-4 py-2 rounded-lg text-sm font-medium transition-opacity hover:opacity-90";

const statusBadge = (active) =>
  active
    ? "px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700 whitespace-nowrap"
    : "px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-500 whitespace-nowrap";

const isActive = (status) => status?.toString().trim().toLowerCase() === "active";

const emptyForm = () => ({ name: "", active: true });

// ════════════════════════════════════════════════════════════════════════════
export default function AssetCategoryModule() {
  const {
    categories,
    loading,
    error,
    fetchCategories,
    createCategory,
    updateCategory,
    deleteCategory,
  } = useAssetCategories();

  const { canDo } = useAuth();

  useEffect(() => {
    fetchCategories();
  }, []);

  const getUserId = () => {
    const storedUser = localStorage.getItem("user");
    if (!storedUser) return null;
    try {
      const user = JSON.parse(storedUser);
      return user?.id || null;
    } catch (e) {
      return null;
    }
  };

  const [search, setSearch]         = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  const [addModal, setAddModal]       = useState(false);
  const [editModal, setEditModal]     = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [viewModal, setViewModal]     = useState(false);
  const [selected, setSelected]       = useState(null);

  const [form, setForm]   = useState(emptyForm());
  const [errors, setErrors] = useState({});

  // ── stats ──────────────────────────────────────────────────────────────
  const totalCats    = categories?.length || 0;
  const activeCats   = categories?.filter(c => isActive(c.status)).length || 0;
  const inactiveCats = categories?.filter(c => !isActive(c.status)).length || 0;

  // ── filtered ───────────────────────────────────────────────────────────
  const filtered = useMemo(() => categories?.filter(c => {
    const matchStatus =
      statusFilter === "All" ||
      (statusFilter === "Active" ? isActive(c.status) : !isActive(c.status));

    const matchSearch =
      (c.name || "").toLowerCase().includes(search.toLowerCase());

    return matchStatus && matchSearch;
  }) || [], [categories, search, statusFilter]);

  // ── pagination ─────────────────────────────────────────────────────────
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  useEffect(() => { setPage(1); }, [search, statusFilter]);
  const totalPages = Math.max(Math.ceil(filtered.length / rowsPerPage), 1);
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const currentRows = filtered.slice(startIndex, startIndex + rowsPerPage);

  // ── validation ─────────────────────────────────────────────────────────
  const validate = (f, editId = null) => {
    const e = {};
    if (!f.name.trim()) {
      e.name = "Category name is required";
    } else if (
      categories?.find(
        c => c.name.toLowerCase() === f.name.trim().toLowerCase() && c.id !== editId
      )
    ) {
      e.name = "A category with this name already exists";
    }
    return e;
  };

  // ── CRUD handlers ──────────────────────────────────────────────────────
  const openAdd = () => { setForm(emptyForm()); setErrors({}); setAddModal(true); };

  const handleAdd = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    if (Object.keys(errs).length) return setErrors(errs);

    const userId = getUserId();

    await createCategory({
      name: form.name.trim(),
      status: form.active ? "Active" : "Inactive",
      create_audit_id: userId,
    });

    setAddModal(false);
  };

  const openEdit = (c) => {
    setSelected(c);
    setForm({ name: c.name, active: isActive(c.status) });
    setErrors({});
    setEditModal(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    const errs = validate(form, selected.id);
    if (Object.keys(errs).length) return setErrors(errs);

    const userId = getUserId();

    await updateCategory(selected.id, {
      name: form.name.trim(),
      status: form.active ? "Active" : "Inactive",
      update_audit_id: userId,
    });

    setEditModal(false);
  };

  const toggleStatus = async (c) => {
    const userId = getUserId();
    await updateCategory(c.id, {
      name: c.name,
      status: isActive(c.status) ? "Inactive" : "Active",
      update_audit_id: userId,
    });
  };

  const confirmDelete = (c) => { setSelected(c); setDeleteModal(true); };

const handleDelete = async () => {
  const user = JSON.parse(localStorage.getItem("user"));

  await deleteCategory(selected.id, {
    delete_audit_id: user.id,
  });

  setDeleteModal(false);
};

  // ── shared field style ─────────────────────────────────────────────────
  const inputCls = (err) =>
    `border ${err ? "border-red-400" : "border-gray-300"} rounded-lg p-2 w-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-300`;

  return (
    <div className="bg-sky-50 min-h-screen">
      <div className="px-4 sm:px-6 lg:px-9 py-6">

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Asset Categories</h1>
          <p className="text-sm text-gray-400 mt-0.5">
            Group asset types into broad categories for easier organisation and reporting
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
          {[
            { label: "Total Categories", value: totalCats,    icon: <Layers size={18}/>,     color: "text-blue-600 bg-blue-50" },
            { label: "Active",           value: activeCats,   icon: <ShieldCheck size={18}/>, color: "text-green-600 bg-green-50" },
            { label: "Inactive",         value: inactiveCats, icon: <Ban size={18}/>,         color: "text-gray-500 bg-gray-100" },
          ].map(s => (
            <div key={s.label} className="bg-white rounded-xl p-4 flex items-center gap-3 shadow-sm border border-gray-100">
              <div className={`p-2 rounded-lg ${s.color}`}>{s.icon}</div>
              <div>
                <p className="text-xs text-gray-400">{s.label}</p>
                <p className="text-lg font-bold text-gray-800">{s.value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Toolbar */}
        <div className="bg-white rounded-xl py-3 px-4 flex flex-col sm:flex-row gap-2 mb-4 shadow-sm">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15}/>
            <input
              type="text"
              placeholder="Search by name"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full sm:w-36 focus:outline-none focus:ring-2 focus:ring-blue-300"
          >
            <option>All</option>
            <option>Active</option>
            <option>Inactive</option>
          </select>
          {(search || statusFilter !== "All") && (
            <button
              type="button"
              onClick={() => {
                setSearch("");
                setStatusFilter("All");
              }}
              className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 transition whitespace-nowrap shrink-0 w-full sm:w-auto"
            >
              <X size={14} />
              Clear Filters
            </button>
          )}
          {canDo("Asset Categories", "Add") && (
          <button
            onClick={openAdd}
            className={`${gradientBtn} flex items-center gap-1.5 justify-center sm:ml-auto`}
            style={gradientStyle}
          >
            <Plus size={16}/> Add Category
          </button>
          )}
        </div>

        {/* {loading && (
          <p className="text-sm text-gray-400 mb-3">Loading categories…</p>
        )}
        {error && (
          <p className="text-sm text-red-500 mb-3">{error}</p>
        )} */}

        {/* Desktop Table */}
        <div className="hidden md:block bg-white shadow-md rounded-2xl p-4">
          <div className="overflow-x-auto">
          <table className="w-full text-sm text-left border-collapse">
            <thead className="bg-sky-50 text-gray-600">
              <tr>
                {["Id", "Name", "Status", ...(canDo("Asset Categories", "Edit") ||
  canDo("Asset Categories", "Delete")
    ? ["Actions"]
    : []),].map(h => (
                  <th key={h} className="p-3 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {currentRows.length > 0 ? currentRows.map((c, idx) => (
                <tr key={c.id} className={`border-b hover:bg-gray-50 transition-colors ${idx % 2 !== 0 ? "bg-[#f7f6fe]" : ""}`}>
                  <td className="p-3 text-gray-400 text-xs">{startIndex + idx + 1}</td>
                  <td className="p-3 text-gray-800 whitespace-nowrap">{c.name}</td>
                  <td className="p-3">
                    <span
                      title={isActive(c.status) ? "Deactivate" : "Activate"}
                      onClick={() => toggleStatus(c)}
                      className={`${statusBadge(isActive(c.status))} cursor-pointer`}
                    >
                      {isActive(c.status) ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="p-3">
                    {(canDo("Asset Categories", "Edit") ||
  canDo("Asset Categories", "Delete")) && (
                    <div className="flex gap-3 items-center">
                      {canDo("Asset Categories", "Edit") && (
                      <button title="Edit" className="text-blue-600 hover:text-blue-800" onClick={() => openEdit(c)}>
                        <Edit2 size={15}/>
                      </button>
                      )}
                      {canDo("Asset Categories", "Delete") && (
                      <button
                        title={c.typeCount > 0 ? "Cannot delete — has linked types" : "Delete"}
                        className={c.typeCount > 0 ? "text-gray-300 cursor-not-allowed" : "text-red-500 hover:text-red-700"}
                        onClick={() => !(c.typeCount > 0) && confirmDelete(c)}
                        disabled={c.typeCount > 0}
                      >
                        <Trash2 size={15}/>
                      </button>
                      )}
                    </div>
  )}
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={4} className="text-center py-12 text-gray-400">
                    <FolderOpen size={32} className="mx-auto mb-2 opacity-30"/>
                    No categories match your filters
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
          <div>
            <Pagination page={currentPage} setPage={setPage} totalPages={totalPages} rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage} />
          </div>
        </div>

        {/* Mobile & Tablet Cards */}
        <div className="md:hidden space-y-3">
          {filtered.length === 0 && (
            <div className="text-center py-10 text-gray-400 bg-white rounded-xl">
              No categories found
            </div>
          )}

          {currentRows.map(c => (
            <div
              key={c.id}
              className="bg-white rounded-xl shadow-sm p-4 border border-gray-100"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <p className="font-semibold text-gray-800 text-sm">
                    {c.name}
                  </p>
                </div>

                <span
                  title={isActive(c.status) ? "Deactivate" : "Activate"}
                  onClick={() => toggleStatus(c)}
                  className={`${statusBadge(isActive(c.status))} cursor-pointer`}
                >
                  {isActive(c.status) ? "Active" : "Inactive"}
                </span>
              </div>

              <div className="flex justify-end gap-4 items-center mt-4 pt-3 border-t border-gray-100">
                <button
                  title="Edit"
                  className="text-blue-600 hover:text-blue-800"
                  onClick={() => openEdit(c)}
                >
                  <Edit2 size={15} />
                </button>

                <button
                  title={
                    c.typeCount > 0
                      ? "Cannot delete — has linked types"
                      : "Delete"
                  }
                  className={
                    c.typeCount > 0
                      ? "text-gray-300 cursor-not-allowed"
                      : "text-red-500 hover:text-red-700"
                  }
                  onClick={() => !(c.typeCount > 0) && confirmDelete(c)}
                  disabled={c.typeCount > 0}
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
          {filtered.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm px-4 pb-4">
              <Pagination page={currentPage} setPage={setPage} totalPages={totalPages} rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage} />
            </div>
          )}
        </div>

        {filtered.some(c => c.typeCount > 0) && (
          <p className="text-xs text-gray-400 mt-3 text-right">
            * Categories with linked asset types cannot be deleted — remove or reassign those types first
          </p>
        )}
      </div>

      {/* ══ ADD MODAL ══════════════════════════════════════════════════════ */}
      {addModal && (
        <Modal title="Add Category" onClose={() => setAddModal(false)}>
          <form onSubmit={handleAdd} className="space-y-4">
            <FormField label="Category Name *" error={errors.name}>
              <input
                type="text"
                placeholder="e.g. Networking"
                value={form.name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, name: e.target.value }))
                }
                className={inputCls(errors.name)}
              />
              {errors.name && (
                <p className="text-xs text-red-500 mt-1">{errors.name}</p>
              )}
            </FormField>

            <FormField label="Status">
              <StatusToggle
                active={form.active}
                onToggle={() =>
                  setForm((f) => ({ ...f, active: !f.active }))
                }
              />
            </FormField>

            <ModalActions
              onCancel={() => setAddModal(false)}
              submitLabel="Add Category"
            />
          </form>
        </Modal>
      )}

      {/* ══ EDIT MODAL ═════════════════════════════════════════════════════ */}
      {editModal && selected && (
        <Modal title={`Edit — ${selected.name}`} onClose={() => setEditModal(false)}>
          <form onSubmit={handleEdit} className="space-y-4">
            <FormField label="Category Name *" error={errors.name}>
              <input
                type="text"
                value={form.name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, name: e.target.value }))
                }
                className={inputCls(errors.name)}
              />
              {errors.name && (
                <p className="text-xs text-red-500 mt-1">{errors.name}</p>
              )}
            </FormField>

            <FormField label="Status">
              <StatusToggle
                active={form.active}
                onToggle={() =>
                  setForm((f) => ({ ...f, active: !f.active }))
                }
              />
            </FormField>

            <ModalActions
              onCancel={() => setEditModal(false)}
              submitLabel="Save Changes"
            />
          </form>
        </Modal>
      )}

      {/* ══ DELETE MODAL ═══════════════════════════════════════════════════ */}
      {deleteModal && selected && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-lg p-6 w-full sm:max-w-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Delete Category</h3>
            <p className="text-sm text-gray-500 mb-5">
              Permanently remove <strong>"{selected.name}"</strong>? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteModal(false)} className="border px-4 py-2 rounded-lg text-sm hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleDelete} className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-red-700">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Shared sub-components ────────────────────────────────────────────────
// Pagination — same look as the Letters page
const navBtn = "px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors";

function Pagination({ page, setPage, totalPages, rowsPerPage, setRowsPerPage }) {
  const start = Math.max(page - 1, 1);
  const end = Math.min(start + 2, totalPages);
  return (
    <div className="flex flex-col sm:flex-row justify-between items-center mt-4 pt-4 border-t border-gray-100 gap-3">
      <div className="text-sm text-gray-500 flex items-center gap-2">
        <span>Rows per page:</span>
        <select
          className="border border-gray-200 bg-gray-50 rounded-lg px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-[#2563eb]/40 focus:border-[#2563eb]/40 transition"
          value={rowsPerPage}
          onChange={e => { setRowsPerPage(Number(e.target.value)); setPage(1); }}
        >
          {[5, 10, 15].map(n => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>
      <div className="flex items-center space-x-1">
        <button onClick={() => setPage(1)} disabled={page === 1} className={navBtn}><ChevronsLeft size={16} /></button>
        <button onClick={() => setPage(Math.max(page - 1, 1))} disabled={page === 1} className={navBtn}><MdKeyboardArrowLeft size={16} /></button>
        {Array.from({ length: end - start + 1 }, (_, i) => start + i).map(n => (
          <button key={n} onClick={() => setPage(n)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${n === page ? "text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
            style={n === page ? gradientStyle : undefined}
          >{n}</button>
        ))}
        <button onClick={() => setPage(Math.min(page + 1, totalPages))} disabled={page === totalPages} className={navBtn}><MdKeyboardArrowRight size={16} /></button>
        <button onClick={() => setPage(totalPages)} disabled={page === totalPages} className={navBtn}><ChevronsRight size={16} /></button>
      </div>
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-5">
          <h3 className="text-xl font-semibold text-gray-800">{title}</h3>
          <button onClick={onClose}><X size={20} className="text-gray-500"/></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function FormField({ label, error, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      {children}
    </div>
  );
}

function StatusToggle({ active, onToggle }) {
  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={onToggle}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
          active
            ? "border-green-400 bg-green-50 text-green-700"
            : "border-gray-300 bg-gray-50 text-gray-500"
        }`}
      >
        {active ? <CheckCircle size={15}/> : <XCircle size={15}/>}
        {active ? "Active" : "Inactive"}
      </button>
      <span className="text-xs text-gray-400">Toggle to change status</span>
    </div>
  );
}

function ModalActions({ onCancel, submitLabel }) {
  return (
    <div className="flex justify-end gap-3 pt-1">
      <button type="button" onClick={onCancel} className="border px-4 py-2 rounded-lg text-sm hover:bg-gray-50">
        Cancel
      </button>
      <button type="submit" className={gradientBtn} style={gradientStyle}>
        {submitLabel}
      </button>
    </div>
  );
}